// src/components/Services/ServiceOrders/ServiceOrdersListView.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useServiceOrders } from '@/hooks/useServiceOrders';
import { ServiceOrdersTable } from './ServiceOrdersTable';
import { ServiceOrderStatus, SERVICE_ORDER_STATUS_LABELS } from '@/types/service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Search } from 'lucide-react';
import { useDebounce } from '@/hooks/useDebounce';

export function ServiceOrdersListView() {
  const { canAccess } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ServiceOrderStatus | 'all'>('all');
  const [page, setPage]     = useState(1);

  const debouncedSearch = useDebounce(search, 400);

  const { data, isLoading } = useServiceOrders({
    status:   status === 'all' ? undefined : status,
    search:   debouncedSearch || undefined,
    page,
    pageSize: 25,
  });

  const orders     = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / 25);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar OS, cliente, responsável..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => { setStatus(v as ServiceOrderStatus | 'all'); setPage(1); }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {(Object.keys(SERVICE_ORDER_STATUS_LABELS) as ServiceOrderStatus[]).map(
                (statusKey) => (
                  <SelectItem key={statusKey} value={statusKey}>
                    {SERVICE_ORDER_STATUS_LABELS[statusKey]}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>

        {canAccess('services:create') && (
          <Button onClick={() => navigate('/dashboard/services/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Nova OS
          </Button>
        )}
      </div>

      {/* Contagem */}
      {!isLoading && (
        <p className="text-sm text-muted-foreground">
          {totalCount} {totalCount === 1 ? 'ordem encontrada' : 'ordens encontradas'}
        </p>
      )}

      {/* Tabela */}
      <ServiceOrdersTable
        orders={orders}
        onViewDetails={(id) => navigate(`/dashboard/services/${id}`)}
        isLoading={isLoading}
      />

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
